import {
  useCallback,
  useId,
  useRef,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { cx } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { Spinner } from '@/shared/ui/Spinner'
import s from './Input.module.css'

/** Ступени высоты: sm — поле в строке таблицы (32), md — форма и поиск (38), lg — глобальный поиск (44). */
export type InputSize = 'sm' | 'md' | 'lg'

/** `search` — капсула поиска и полей-триггеров; `field` — прямоугольник для ввода данных. */
export type InputVariant = 'field' | 'search'

/** `end` — числовая колонка таблицы: цена, сумма, количество. */
export type InputAlign = 'start' | 'end'

/** `mono` — поля с маской и кодами: ИНН, КПП, телефон, артикул, серийный номер. */
export type InputFont = 'ui' | 'mono'

export type InputProps = {
  size?: InputSize
  variant?: InputVariant
  align?: InputAlign
  font?: InputFont
  /** Слот перед полем: иконка лупы, код страны, символ валюты слева. */
  prefix?: ReactNode
  /** Слот после поля: единица «₽»/«%», кнопка очистки, кнопка-действие, подсказка ⌘K. */
  suffix?: ReactNode
  /** Идёт серверная проверка: спиннер и бегущая полоса. Ввод при этом не блокируется. */
  loading?: boolean
  /** Значение подтверждено сервером: зелёная рамка, подложка и галка в суффиксе. */
  confirmed?: boolean
  /** Только вид ошибки, без сообщения: текст рисует FormField или сводка формы. */
  invalid?: boolean
  /** Сообщение об ошибке под полем. Непустое значение включает `invalid`. */
  error?: ReactNode
  /** Указывает на сам input — так работают register() из react-hook-form и программный фокус. */
  ref?: Ref<HTMLInputElement>
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'children'>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const SIZE_CLASS: Record<InputSize, string | undefined> = {
  sm: s.sm,
  md: s.md,
  lg: s.lg,
}

const VARIANT_CLASS: Record<InputVariant, string | undefined> = {
  field: s.field,
  search: s.search,
}

const ALIGN_CLASS: Record<InputAlign, string | undefined> = {
  start: s.alignStart,
  end: s.alignEnd,
}

const FONT_CLASS: Record<InputFont, string | undefined> = {
  ui: s.fontUi,
  mono: s.fontMono,
}

/**
 * Однострочное поле ввода: поиск по каталогу, реквизиты контрагента, ячейка редактируемой таблицы.
 *
 * Значение компонент не хранит и не преобразует — работают оба нативных режима (`value` + `onChange`
 * и `defaultValue`), а `{...register()}` из react-hook-form доходит до input без Controller.
 * Кнопка внутри поля лежит соседом input, а не внутри него: вложенной интерактивности не возникает.
 * Контракт: docs/ui-kit/contracts/input.md
 */
export function Input({
  size = 'md',
  variant = 'field',
  align = 'start',
  font = 'ui',
  prefix,
  suffix,
  loading = false,
  confirmed = false,
  invalid = false,
  error,
  disabled = false,
  readOnly = false,
  className,
  ref,
  'aria-describedby': describedBy,
  ...rest
}: InputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const errorId = `${useId()}-error`

  const hasError = Boolean(error)
  const isInvalid = invalid || hasError
  const hasStatus = loading || confirmed
  const hasSuffix = hasStatus || (suffix !== undefined && suffix !== null)

  const attachRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node

      // ref-колбэк потребителя в React 19 вправе вернуть cleanup — его нельзя терять
      if (typeof ref === 'function') return ref(node)
      if (ref) ref.current = node
      return undefined
    },
    [ref],
  )

  /*
   * Рамка шире input: по краям 12-16px отступа плюс слоты. Клик по этой зоне обязан ставить
   * курсор в поле, как у нативного input, у которого рамка и текст — один элемент.
   *
   * Всплытие не глушится: событие уходит наверх целиком, и делегирование у родителя (строка
   * таблицы, закрытие поповера по клику вне) продолжает работать. Собственный интерактив слота
   * распознаётся по наличию фокуса у цели, а не по имени тега, — тогда любой контрол в слоте
   * останется рабочим. Обработчик потребителя здесь не вызывается: он уже висит на самом input
   * через {...rest}, и второй вызов на всплытии удвоил бы его.
   */
  const handleMouseDown = (event: MouseEvent<HTMLSpanElement>) => {
    const node = event.target
    if (disabled || event.defaultPrevented || !(node instanceof HTMLElement)) return
    if (node.closest('button, a, input, select, textarea, [tabindex]')) return

    // иначе браузер снимет фокус с input сразу после установки
    event.preventDefault()
    inputRef.current?.focus()
  }

  return (
    <span
      className={cx(
        s.root,
        SIZE_CLASS[size],
        VARIANT_CLASS[variant],
        ALIGN_CLASS[align],
        FONT_CLASS[font],
        hasSuffix && s.withSuffix,
        confirmed && s.confirmed,
        isInvalid && s.invalid,
        loading && s.loading,
        readOnly && s.readOnly,
        disabled && s.disabled,
        className,
      )}
      data-size={size}
      data-variant={variant}
    >
      <span className={s.box} onMouseDown={handleMouseDown}>
        {prefix !== undefined && prefix !== null ? (
          <span className={s.prefix}>{prefix}</span>
        ) : null}

        <input
          {...rest}
          ref={attachRef}
          className={s.input}
          disabled={disabled}
          readOnly={readOnly}
          aria-busy={loading || undefined}
          aria-invalid={isInvalid || undefined}
          aria-describedby={cx(describedBy, hasError && errorId) || undefined}
        />

        {hasSuffix ? (
          <span className={s.suffix}>
            {suffix}
            {/* Спиннер и галка декоративны: о проверке и о найденном контрагенте
                сообщает подпись под полем, дублировать её голосом не нужно */}
            {loading ? <Spinner size="xs" /> : null}
            {!loading && confirmed ? (
              <span className={s.confirmedMark}>
                <Icon name="check" size="sm" />
              </span>
            ) : null}
          </span>
        ) : null}

        {loading ? <span className={s.bar} aria-hidden="true" /> : null}
      </span>

      {hasError ? (
        <span className={s.error} id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </span>
  )
}
